import { format } from 'date-fns';
import React, { useCallback } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { ToolTip, hitSlopS, rem } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import CardRow from 'design-system/CardRow';
import CardUpgradeRow from 'design-system/CardUpgradeRow';
import Text from 'design-system/Text';
import IconTooltip from 'design-system/icons/IconTooltip';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import { commonToolTipStrings } from '../constants';
import { selectShowLowerFxFeeContent } from '../reducers/selectors';
import { formatCurrencyExchangeRate, formatExpiryDate, formatNumberOfShares } from '../utils';
import { FxFeeBreakdownModal } from './FxFeeBreakdownModal';
const strings = {
    wantLowerFxFee: 'Want a lower FX fee?',
    row: {
        completeTime: 'Time',
        clientName: 'Client name',
        orderId: 'Order ID',
        limitPrice: 'Limit price',
        stopPrice: 'Stop price',
        numberOfShares: 'No. of shares',
        numberOfSharesEst: 'No. of shares (est.)',
        pricePerShare: 'Price per share',
        lastClosePrice: 'Last close price',
        alpacaCommission: 'Commission',
        tradingCharge: 'Trading charge',
        fxRate: 'FX rate',
        fxRateBase: 'Base FX rate',
        fxFee: 'FX fee',
        fxFeeEst: 'FX fee (est.)',
        fxCommission: 'Commission',
        totalCost: 'Total cost',
        totalAmount: 'Total amount',
        totalAmountEst: 'Total amount (est.)',
        expireTime: 'Expiration',
    },
};
export const Row = React.memo(({ bold, title, info }) => (<CardRow small tableSpacing title={bold && typeof title === 'string' ? <Text Text-14>{title}</Text> : title} info={info}/>));
const formatRate = ({ from, to }) => formatCurrencyExchangeRate(from, to);
export const ReceiptRows = React.memo(({ from, to, completeTime, clientName, orderId, limitPrice, stopPrice, numOfShares, numOfSharesEst, pricePerShare, lastClosePrice, alpacaCommission, tradingCharge, totalCost, totalAmount, totalAmountEst, fxRate, fxCommission, fxRateBase, fxFee, fxFeeEst, expireTime, }) => {
    const showLowerFxFeeCard = useAppSelector(selectShowLowerFxFeeContent);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const privText = useCallback((s, isNumber) => (<BlurAmountView enabled={scrambled}>
          <Text {...(isNumber ? { 'Numbers-14': true } : { 'Text-14': true })}>{s}</Text>
        </BlurAmountView>), [scrambled]);
    const privAmount = useCallback((amount) => (<BlurAmountView enabled={scrambled}>
          <Text Numbers-14>{formatAmount(amount.value, amount.currency, 2)}</Text>
        </BlurAmountView>), [scrambled]);
    const showFxFeeBreakdownModal = useCallback((fee, feeTitle) => () => {
        FxFeeBreakdownModal.show({
            showLowerFxFees: showLowerFxFeeCard,
            fxRateTitle: strings.row.fxRate,
            fxRateInfo: fxRate ? formatRate(fxRate) : '',
            baseRateTitle: strings.row.fxRateBase,
            baseRateInfo: fxRateBase ? formatRate(fxRateBase) : '',
            fxFeeTitle: feeTitle,
            fxFee: formatAmount(fee.value, fee.currency, 2),
        });
    }, [fxRate, fxRateBase, showLowerFxFeeCard]);
    return (<>
        {!!from && <Row title="From" info={from}/>}
        {!!to && <Row title="To" info={to}/>}
        {completeTime != null && (<Row title={strings.row.completeTime} info={format(completeTime, 'd MMM yyyy, h:mmaaa')}/>)}
        {clientName != null && <Row title={strings.row.clientName} info={clientName}/>}
        {orderId != null && <Row title={strings.row.orderId} info={orderId}/>}
        {limitPrice != null && <Row title={strings.row.limitPrice} info={privAmount(limitPrice)}/>}
        {stopPrice != null && <Row title={strings.row.stopPrice} info={privAmount(stopPrice)}/>}
        {numOfShares != null && (<Row title={strings.row.numberOfShares} info={privText(formatNumberOfShares(numOfShares, false, false))}/>)}
        {numOfSharesEst != null && (<Row title={<View style={styles.row}>
                <Text TextThin-14 Gray>
                  {strings.row.numberOfSharesEst}
                </Text>
                <ToolTip title={commonToolTipStrings.noOfSharesTitle} subtitle={commonToolTipStrings.noOfSharesWithoutFxFeeDesc} colorKey="secondary" small/>
              </View>} info={privText(formatNumberOfShares(numOfSharesEst, false, false))}/>)}
        {pricePerShare != null && <Row title={strings.row.pricePerShare} info={privAmount(pricePerShare)}/>}
        {lastClosePrice != null && <Row title={strings.row.lastClosePrice} info={privAmount(lastClosePrice)}/>}
        {fxRate != null && <Row title={strings.row.fxRate} info={privText(formatRate(fxRate), true)}/>}
        {fxCommission != null && <Row title={strings.row.fxCommission} info={privAmount(fxCommission)}/>}
        {fxRateBase != null && <Row title={strings.row.fxRateBase} info={privText(formatRate(fxRateBase), true)}/>}
        {fxFee != null && (<Row title={<View style={styles.row}>
                <Text TextThin-14 Gray>
                  {strings.row.fxFee}
                </Text>
                <TouchableOpacity onPress={showFxFeeBreakdownModal(fxFee, strings.row.fxFee)} hitSlop={hitSlopS}>
                  <IconTooltip height={16} width={16} colorKey="secondary"/>
                </TouchableOpacity>
              </View>} info={privAmount(fxFee)}/>)}
        {fxFeeEst != null && (<Row title={<View style={styles.row}>
                <Text TextThin-14 Gray>
                  {strings.row.fxFeeEst}
                </Text>
                <TouchableOpacity onPress={showFxFeeBreakdownModal(fxFeeEst, strings.row.fxFeeEst)} hitSlop={hitSlopS}>
                  <IconTooltip height={16} width={16} colorKey="secondary"/>
                </TouchableOpacity>
              </View>} info={privAmount(fxFeeEst)}/>)}
        {showLowerFxFeeCard === true && !!(fxCommission || fxFee || fxFeeEst) && (<CardUpgradeRow title={strings.wantLowerFxFee} withHorizontalMargins/>)}
        {alpacaCommission != null && (<Row title={<View style={styles.row}>
                <Text TextThin-14 Gray>
                  {strings.row.alpacaCommission}
                </Text>
                <ToolTip title={commonToolTipStrings.commissionTitle} subtitle={commonToolTipStrings.commissionDesc} colorKey="secondary" small/>
              </View>} info={privAmount(alpacaCommission)}/>)}
        {tradingCharge != null && (<Row title={<View style={styles.row}>
                <Text TextThin-14 Gray>
                  {strings.row.tradingCharge}
                </Text>
                <ToolTip title={commonToolTipStrings.tradingChargeTitle} subtitle={commonToolTipStrings.tradingChargeDesc} colorKey="secondary" small/>
              </View>} info={privAmount(tradingCharge)}/>)}
        {totalCost != null && <Row bold title={strings.row.totalCost} info={privAmount(totalCost)}/>}
        {totalAmount != null && <Row bold title={strings.row.totalAmount} info={privAmount(totalAmount)}/>}
        {totalAmountEst != null && <Row bold title={strings.row.totalAmountEst} info={privAmount(totalAmountEst)}/>}
        {expireTime != null && <Row title={strings.row.expireTime} info={formatExpiryDate(expireTime)}/>}
      </>);
});
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        gap: rem(4),
        alignItems: 'center',
    },
});
